import { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Pressable, Image, ScrollView } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";

const SCALE_STEPS = [0.5, 0.75, 1, 1.5, 2, 3];

type FitMode = "width" | "height" | "screen" | "manual";

interface ImagePreviewProps {
  url: string;
  name: string;
  onClose: () => void;
  onOpenInNewTab: () => void;
}

export function ImagePreview({ url, name, onClose, onOpenInNewTab }: ImagePreviewProps) {
  const { colors } = useTheme();
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>("screen");
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  const TOOLBAR_HEIGHT = 52;

  const onContainerLayout = useCallback((e: { nativeEvent: { layout: { width: number; height: number } } }) => {
    const { width, height } = e.nativeEvent.layout;
    setContainerSize({ w: width, h: height });
  }, []);

  const containerWidth = containerSize.w;
  const containerHeight = containerSize.h - TOOLBAR_HEIGHT;

  useEffect(() => {
    Image.getSize(
      url,
      (w, h) => setNaturalSize({ w, h }),
      () => setNaturalSize({ w: 600, h: 600 }),
    );
  }, [url]);

  const getImageDimensions = () => {
    if (!naturalSize || containerWidth === 0) {
      return { width: 300, height: 300 };
    }
    const { w: natW, h: natH } = naturalSize;
    const aspect = natW / natH;

    switch (fitMode) {
      case "width": {
        const w = containerWidth * scale;
        return { width: w, height: w / aspect };
      }
      case "height": {
        const h = containerHeight * scale;
        return { width: h * aspect, height: h };
      }
      case "screen": {
        const fitScale = Math.min(containerWidth / natW, containerHeight / natH);
        const w = natW * fitScale * scale;
        const h = natH * fitScale * scale;
        return { width: w, height: h };
      }
      case "manual": {
        return { width: natW * scale, height: natH * scale };
      }
    }
  };

  const zoomIn = () => {
    const nextIdx = SCALE_STEPS.findIndex((s) => s > scale);
    if (nextIdx !== -1) {
      setScale(SCALE_STEPS[nextIdx]);
      if (fitMode === "screen") setFitMode("manual");
    }
  };

  const zoomOut = () => {
    const prevIdx = [...SCALE_STEPS].reverse().findIndex((s) => s < scale);
    if (prevIdx !== -1) {
      setScale([...SCALE_STEPS].reverse()[prevIdx]);
      if (fitMode === "screen") setFitMode("manual");
    }
  };

  const fitToScreen = () => {
    setScale(1);
    setFitMode("screen");
  };

  const fitToWidth = () => {
    setScale(1);
    setFitMode("width");
  };

  const fitToHeight = () => {
    setScale(1);
    setFitMode("height");
  };

  const dims = getImageDimensions();
  const scaleLabel = `${Math.round(scale * 100)}%`;

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]} onLayout={onContainerLayout}>
      {/* Toolbar */}
      <View style={[styles.toolbar, { backgroundColor: colors.background }]}>
        <ThemedText style={[styles.toolbarFileName, { color: colors.text }]} numberOfLines={1}>
          {name}
        </ThemedText>
        <View style={styles.toolbarControls}>
          <Pressable onPress={zoomOut} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="magnify-minus-outline" size={22} color={colors.textSecondary} />
          </Pressable>
          <ThemedText style={[styles.scaleLabel, { color: colors.text }]}>{scaleLabel}</ThemedText>
          <Pressable onPress={zoomIn} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="magnify-plus-outline" size={22} color={colors.textSecondary} />
          </Pressable>
          <View style={[styles.separator, { backgroundColor: colors.border }]} />
          <Pressable
            onPress={fitToWidth}
            style={[styles.toolbarButton, fitMode === "width" && { backgroundColor: colors.primary + "33" }]}
          >
            <MaterialCommunityIcons name="arrow-expand-horizontal" size={22} color={colors.textSecondary} />
          </Pressable>
          <Pressable
            onPress={fitToHeight}
            style={[styles.toolbarButton, fitMode === "height" && { backgroundColor: colors.primary + "33" }]}
          >
            <MaterialCommunityIcons name="arrow-expand-vertical" size={22} color={colors.textSecondary} />
          </Pressable>
          <Pressable
            onPress={fitToScreen}
            style={[styles.toolbarButton, fitMode === "screen" && { backgroundColor: colors.primary + "33" }]}
          >
            <MaterialCommunityIcons name="fit-to-screen-outline" size={22} color={colors.textSecondary} />
          </Pressable>
          <View style={[styles.separator, { backgroundColor: colors.border }]} />
          <Pressable onPress={onOpenInNewTab} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="open-in-new" size={22} color={colors.textSecondary} />
          </Pressable>
          <Pressable onPress={onClose} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="close" size={22} color={colors.textSecondary} />
          </Pressable>
        </View>
      </View>

      {/* Image */}
      <ScrollView horizontal style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <ScrollView contentContainerStyle={styles.scrollContentInner}>
          <Image source={{ uri: url }} style={{ width: dims.width, height: dims.height }} resizeMode="contain" />
        </ScrollView>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 12,
    overflow: "hidden",
  },
  toolbar: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    gap: 12,
  },
  toolbarFileName: {
    flex: 1,
    fontSize: 14,
    fontWeight: "500",
  },
  toolbarControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  toolbarButton: {
    padding: 6,
    borderRadius: 4,
  },
  separator: {
    width: 1,
    height: 20,
    marginHorizontal: 4,
  },
  scaleLabel: {
    fontSize: 13,
    minWidth: 40,
    textAlign: "center",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  scrollContentInner: {
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
  },
});
